import React, { useState, useEffect } from "react";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import log from "cslog";
import {
    Button,
    Collapse,
    Form,
    Input,
    Select,
    Radio,
    Spin,
    message,
    Row,
    Space,
} from "antd";
import styles from "./styles/PresetForm.module.css";
import { MessageCodeView } from "../ai_home/ChatBox";
import { getHeroIcon } from "../_helpers/heroicons";
import { useCreateItem } from "../_actions/mutations/items";
import { history } from "../_helpers/history";
import { useParams } from "react-router-dom";
import { useSingleItem } from "../_actions/app/items";
import { useUpdateItemMutation } from "../_actions/app/items";
import { useItemsFine } from "../_actions/app/items";
import snakeCase from "lodash/snakeCase";
import PresetGenModal from "./PresetGenModal";
import { PlusOutlined, SettingOutlined } from "@ant-design/icons";
import RelationField from "../forms_standard/elements/RelationField";
import RelationFieldStanard from "../qforms/standard_elements/RelationFieldStandard";

const { TextArea } = Input;

function PresetForm() {
    const { id } = useParams(); // Get preset ID from URL if in edit mode
    const isEditMode = !!id;

    const [form] = Form.useForm();
    const [userInputs, setUserInputs] = useState([]);
    const [schema, setSchema] = useState([]);
    const [generatedSchema, setGeneratedSchema] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [isLoading, setIsLoading] = useState(isEditMode);

    const [origin_preset, setOriginPreset] = useState(null);

    // Fetch existing preset data if in edit mode
    const {
        data: presetData,
        isSuccess: fetchSuccess,
        isLoading: isFetching,
    } = useSingleItem(
        "qdb_creation_schema",
        `/${id || origin_preset}?fields=*.*`,
        id !== null
    );

    const {
        data: imageStyleData,
        isSuccess: imageStyleSuccess,
        isLoading: imageStyleLoading,
    } = useItemsFine("qdb_image_style", "fields=*");

    // Create or update mutation based on mode
    const createMutation = useCreateItem("qdb_creation_schema", (data) => {
        console.log("item created successfully", data);
        history.push("/admin-settings/preset-manager", {
            from: history.location.pathname,
        });
    });

    const updateMutation = useUpdateItemMutation();

    const is_origin = origin_preset ? true : false;

    // Load existing preset data into the form when available
    useEffect(() => {
        if ((isEditMode || origin_preset) && fetchSuccess && presetData) {
            setIsLoading(false);

            // Extract schema fields from true_schema
            let extractedSchema = [];
            if (
                presetData.true_schema &&
                presetData.true_schema.schema &&
                presetData.true_schema.schema.properties
            ) {
                extractedSchema = Object.entries(
                    presetData.true_schema.schema.properties
                ).map(([key, value]) => ({
                    name: key,
                    type: value.type || "string",
                    itype:
                        value.itype ||
                        (value.type === "array" ? "array" : "string"),
                    description: value.description || "",
                    by: value.by || "ai",
                    maxChar: value.maxChar,
                    bg_rm: value.bg_rm,
                    image_style: value.image_style,
                    aspect_ratio: value.aspect_ratio,
                    object_position: value.object_position,
                    items: value.items,
                }));
            }
            const mappedInputs = (presetData.inputs || []).map((input) => ({
                name: input.key || input.name || "",
                type: input.type || "text",
                label: input.label || "",
            }));

            setSchema(extractedSchema);
            setUserInputs(mappedInputs);

            // Update form values
            form.setFieldsValue({
                name: presetData.name,
                description: presetData.description,
                type: presetData.type || "singlepage",
                user_inputs: mappedInputs,
                schema: extractedSchema,
            });
        }
    }, [isEditMode, fetchSuccess, presetData, form, origin_preset]);

    const handleCreatePreset = (generatedJson) => {
        const formValues = form.getFieldsValue();
        const generateBlockId = (name) => {
            if (!name) return "default_preset";
            if (is_origin) return presetData?.block_id;
            return name
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_]/g, "");
        };

        const modified_inputs = [];
        formValues.user_inputs.forEach((input) => {
            modified_inputs.push({
                key: input.name,
                type: input.type,
                label: input.label,
            });
        });

        const payload = {
            block_id: isEditMode
                ? presetData.block_id
                : generateBlockId(formValues.name),
            name: formValues.name,
            description: formValues.description,
            type: formValues.type || "singlepage",
            inputs: modified_inputs,
            true_schema: {
                name: isEditMode
                    ? presetData.true_schema.name
                    : generateBlockId(formValues.name),
                schema: {
                    type: "object",
                    properties: generatedJson.schema.properties || {},
                    required: generatedJson.schema.required || [],
                    additionalProperties: false,
                },
                strict: true,
            },
            enabled: isEditMode ? presetData.enabled : true,
            ...(is_origin ? { origin_preset: origin_preset } : {}),
        };

        log.d(payload, "Payload to Move");
        // return;

        if (isEditMode) {
            updateMutation.mutate(
                {
                    collection: "qdb_creation_schema",
                    id: id,
                    data: { ...payload },
                },
                {
                    onSuccess: () => {
                        message.success("Preset updated successfully!");
                        history.push("/design-admin/preset-manager");
                    },
                    onError: (error) => {
                        console.error("Failed to update preset:", error);
                        message.error("Failed to update preset");
                    },
                }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: () => {
                    message.success("Preset created successfully!");
                },
                onError: (error) => {
                    console.error("Failed to create preset:", error);
                    message.error("Failed to create preset");
                },
            });
        }
    };

    const handleAddUserInput = () => {
        const newUserInput = {
            name: ``,
            type: "text",
            label: `Input ${userInputs.length + 1}`,
        };
        const updated = [...userInputs, newUserInput];
        setUserInputs(updated);
        form.setFieldValue("user_inputs", updated);
    };

    const handleAddSchema = () => {
        const newField = {
            name: `field_${schema.length + 1}`,
            type: "text",
            label: `Field ${schema.length + 1}`,
        };
        const updated = [...schema, newField];
        setSchema(updated);
        form.setFieldValue("schema", updated);
    };

    const handleUpdateUserInput = (index, newValue) => {
        const updated = [...userInputs];
        updated[index] = newValue;
        setUserInputs(updated);
        form.setFieldValue("user_inputs", updated);
    };

    const handleUpdateSchema = (index, newValue) => {
        const updated = [...schema];
        updated[index] = newValue;
        setSchema(updated);
        form.setFieldValue("schema", updated);
    };

    const handleRemoveUserInput = (index) => {
        const updated = userInputs.filter((_, i) => i !== index);
        setUserInputs(updated);
        form.setFieldValue("user_inputs", updated);
    };

    const handleRemoveSchema = (index) => {
        const updated = schema.filter((_, i) => i !== index);
        setSchema(updated);
        form.setFieldValue("schema", updated);
    };
    const handleSubmit = () => {
        const formValues = form.getFieldsValue();
        if (!formValues.name) {
            message.error("Please provide a name for the preset");
            return;
        }
        if (!formValues.description) {
            message.error("Please provide a description for the preset");
            return;
        }
        const userInputs = formValues.user_inputs || [];
        const missingNames = userInputs.filter(
            (input) => !input.name || input.name.trim() === ""
        );

        if (missingNames.length > 0) {
            message.error("All user inputs must have a name");
            return;
        }
        const schemaItems = formValues.schema || [];
        const missingSchemaNames = schemaItems.filter(
            (input) => !input.name || input.name.trim() === ""
        );
        if (missingSchemaNames.length > 0) {
            message.error("All Schema fields must have a name");
            return;
        }
        const generatedJson = generateFinalSchema(formValues);
        setGeneratedSchema(generatedJson);
        setShowPreview(true);

        log.d(generatedJson, "Generated Json");

        handleCreatePreset(generatedJson);
    };

    const generateFinalSchema = (formValues) => {
        const name = formValues.name || "custom_preset";
        const description = formValues.description || "";
        const inputs = formValues.user_inputs || [];
        const schemaItems = formValues.schema || [];

        const processProperties = (properties) => {
            if (!properties) return {};
            const cleanProperties = {};

            Object.entries(properties).forEach(([propKey, propValue]) => {
                const { name, _nameChanged, ...propWithoutName } = propValue;
                const newKey =
                    name?.toLowerCase().replace(/\s+/g, "_") || propKey;
                cleanProperties[newKey] = { ...propWithoutName };
                if (
                    (propWithoutName.type === "array" ||
                        propWithoutName.itype === "array") &&
                    propWithoutName.items &&
                    propWithoutName.items.properties
                ) {
                    cleanProperties[newKey].items = {
                        type: propWithoutName.items.type || "object",
                        properties: processProperties(
                            propWithoutName.items.properties
                        ),
                    };
                }
            });

            return cleanProperties;
        };

        const processSchemaItems = (items) => {
            const properties = {};
            const required = [];

            items.forEach((item) => {
                if (!item?.name) return;
                const itemType =
                    item.itype === "array"
                        ? "array"
                        : ["textarea", "text", "image"].includes(item.type)
                        ? "string"
                        : item.type;
                properties[item.name] = {
                    type: itemType,
                    description: item.description || item.name,
                };

                if (item.by) properties[item.name].by = item.by;
                if (item.itype) properties[item.name].itype = item.itype;
                if (item.internal !== undefined)
                    properties[item.name].internal = item.internal;
                if (item.bg_rm !== undefined)
                    properties[item.name].bg_rm = item.bg_rm;
                if (item.image_style)
                    properties[item.name].image_style = item.image_style;
                if (item.aspect_ratio)
                    properties[item.name].aspect_ratio = item.aspect_ratio;
                if (item.object_position)
                    properties[item.name].object_position =
                        item.object_position;
                if (item.maxChar)
                    properties[item.name].maxChar = parseInt(item.maxChar, 10);

                if (
                    (item.type === "array" || item.itype === "array") &&
                    item.items
                ) {
                    const itemCount = Object.keys(
                        item.items.properties || {}
                    ).length;
                    properties[item.name].length = itemCount;

                    properties[item.name].items = {
                        type: item.items.type || "object",
                        properties: processProperties(item.items.properties),
                    };
                }

                required.push(item.name);
            });

            return { properties, required };
        };

        const schemaResult = processSchemaItems(schemaItems);

        return {
            name,
            description,
            inputs,
            schema: {
                type: "object",
                properties: schemaResult.properties,
                required: schemaResult.required,
                additionalProperties: false,
            },
            strict: true,
        };
    };

    if (isLoading) {
        return (
            <QurealSection
                header={
                    <SectionHeader
                        heading="Loading Preset..."
                        icon={<SettingOutlined />}
                    />
                }
            >
                <div className={styles.loadingContainer}>
                    <Spin size="large" />
                </div>
            </QurealSection>
        );
    }

    return (
        <QurealSection
            header={
                <SectionHeader
                    heading={isEditMode ? "Edit Preset" : "Create New Preset"}
                />
            }
        >
            <div className={styles.wrapper}>
                <Space>
                    <PresetGenModal
                        trigger={
                            <Button
                                icon={getHeroIcon("sparkles", {
                                    width: "15px",
                                })}
                            >
                                Generate With AI
                            </Button>
                        }
                    />

                    <RelationFieldStanard
                        // key={render_key}
                        field={{
                            foreign_key_table: "qdb_creation_schema",
                            foreign_key_column: "id",
                            junction_field: "qdb_tag_id",
                            many_table_pk: "id",
                            template: "{{name}}",
                            type: "relationO2M",
                        }}
                        fieldString="&limit=-1&filter[generic][_eq]=true&filter[type][_eq]=singlepage"
                        value={origin_preset}
                        onChange={(val) => {
                            setOriginPreset(val);
                        }}
                        many={false}
                        style={{
                            width: "300px",
                        }}
                        additional_options={[
                            {
                                label: "No origin preset",
                                value: null,
                            },
                        ]}
                        placeholder="Origin Preset"
                    />
                </Space>

                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        type: "singlepage",
                        user_inputs: userInputs,
                        schema: schema,
                    }}
                    style={{
                        width: "600px",
                    }}
                >
                    <Form.Item label="Name" name="name" required>
                        <Input />
                    </Form.Item>

                    <Form.Item label="Description" name="description" required>
                        <TextArea />
                    </Form.Item>

                    <Form.Item label="Creation Type" name="type">
                        <Select
                            options={[
                                { value: "singlepage", label: "Singlepage" },
                                { value: "document", label: "Document" },
                                { value: "letter", label: "Letter" },
                            ]}
                            disabled={is_origin}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Schema (What AI will generate)"
                        name="schema"
                    >
                        <SchemaField
                            value={schema}
                            onAddSchema={handleAddSchema}
                            onUpdateSchema={handleUpdateSchema}
                            onRemoveSchema={handleRemoveSchema}
                            imageStyleData={imageStyleData}
                            imageStyleLoading={imageStyleLoading}
                            imageStyleSuccess={imageStyleSuccess}
                            is_origin={is_origin}
                        />
                    </Form.Item>
                    <Form.Item
                        label="User Inputs (What AI needs from user)"
                        name="user_inputs"
                    >
                        <UserInputsField
                            value={userInputs}
                            onAddInput={handleAddUserInput}
                            onUpdateInput={handleUpdateUserInput}
                            onRemoveInput={handleRemoveUserInput}
                        />
                    </Form.Item>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={
                            createMutation.isLoading || updateMutation.isLoading
                        }
                    >
                        {isEditMode
                            ? "Update Preset"
                            : is_origin
                            ? "Create Child Preset"
                            : "Create Preset"}
                    </Button>
                </Form>
                {showPreview && (
                    <div className={styles.preview}>
                        <MessageCodeView
                            default_show={true}
                            data={{
                                language: "json",
                                code: generatedSchema,
                            }}
                        />
                    </div>
                )}
            </div>
        </QurealSection>
    );
}

function ArrayItemsForm({ value = {}, onChange }) {
    const handleAddProperty = () => {
        const newKey = `property${Object.keys(value).length + 1}`;
        onChange({
            ...value,
            [newKey]: {
                type: "string",
                description: `Property ${Object.keys(value).length + 1}`,
            },
        });
    };

    const handlePropertyChange = (key, newValue) => {
        onChange({
            ...value,
            [key]: newValue,
        });
    };

    const handleDeleteProperty = (key) => {
        const newProperties = { ...value };
        delete newProperties[key];
        onChange(newProperties);
    };

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                }}
            >
                <h4 style={{ margin: 0 }}>Array Items Schema</h4>
                <Button onClick={handleAddProperty} size="small" type="primary">
                    Add Schema
                </Button>
            </div>

            {Object.keys(value).length === 0 ? (
                <div
                    style={{
                        color: "#999",
                        textAlign: "center",
                        padding: "10px",
                    }}
                >
                    No properties defined. Click "Add Property" to add one.
                </div>
            ) : (
                Object.entries(value).map(([key, propValue]) => (
                    <div
                        key={key}
                        style={{
                            marginBottom: "10px",
                            padding: "10px",
                            border: "1px solid #eee",
                            borderRadius: "4px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "10px",
                            }}
                        >
                            <strong>{propValue.name || key}</strong>
                            <Button
                                size="small"
                                danger
                                onClick={() => handleDeleteProperty(key)}
                            >
                                Remove
                            </Button>
                        </div>
                        <SchemaOneField
                            value={propValue}
                            onChange={(updated) =>
                                handlePropertyChange(key, updated)
                            }
                        />
                    </div>
                ))
            )}
        </div>
    );
}

function SchemaField({
    value = [],
    onAddSchema,
    onUpdateSchema,
    onRemoveSchema,
    imageStyleData,
    imageStyleLoading,
    imageStyleSuccess,
    is_origin,
}) {
    return (
        <>
            {value?.length > 0 && (
                <Collapse
                    style={{ marginBottom: "10px" }}
                    accordion={true}
                    items={value.map((item, index) => ({
                        key: `schema-${index}`,
                        label: (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span>
                                    {item?.name || `Field ${index + 1}`}
                                </span>
                                <Button
                                    size="small"
                                    danger
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveSchema(index);
                                    }}
                                    disabled={is_origin}
                                >
                                    {getHeroIcon("minus", { width: "18px" })}
                                </Button>
                            </div>
                        ),
                        children: (
                            <SchemaOneField
                                value={item}
                                onChange={(updatedValue) =>
                                    onUpdateSchema(index, updatedValue)
                                }
                                imageStyleData={imageStyleData}
                                imageStyleLoading={imageStyleLoading}
                                imageStyleSuccess={imageStyleSuccess}
                                is_origin={is_origin}
                            />
                        ),
                    }))}
                />
            )}
            <Button
                onClick={onAddSchema}
                style={{ marginTop: "0px" }}
                icon={<PlusOutlined />}
                disabled={is_origin}
            >
                Add Schema Field
            </Button>
        </>
    );
}

function SchemaOneField({
    value,
    onChange,
    imageStyleData,
    imageStyleLoading,
    imageStyleSuccess,
    is_origin,
}) {
    const [form] = Form.useForm();
    const [showItemsSchema, setShowItemsSchema] = useState(false);
    const [itemsProperties, setItemsProperties] = useState(
        value?.items?.properties || {}
    );
    const currentType = form.getFieldValue("type") || value?.type || "string";
    const currentIType = form.getFieldValue("itype") || value?.itype;
    useEffect(() => {
        const initialType = value?.itype === "array" ? "array" : "string";
        const formValues = { ...value, type: initialType };
        if (value?.itype === "img") {
            formValues.image_type =
                value?.by === "ui" ? "user_uploaded" : "ai_generated";
        }
        form.setFieldsValue(formValues); //hard-coded type=string/array
        setShowItemsSchema(value?.type === "array" || value?.itype === "array");
        setItemsProperties(value?.items?.properties || {});
    }, [form, value]);

    useEffect(() => {
        const itype = form.getFieldValue("itype");
        if (itype) {
            // Set by value based on itype
            if (itype === "string" || itype === "html") {
                form.setFieldValue("by", "ai");
            } else if (itype === "img") {
                // Preserve the existing value for images
                const existingBy = value?.by || form.getFieldValue("by");
                if (existingBy) {
                    form.setFieldValue("by", existingBy);
                }
            }
            if (itype === "array") {
                form.setFieldValue("type", "array");
                setShowItemsSchema(true);

                // Initialize items structure if not exists
                if (!form.getFieldValue("items")) {
                    form.setFieldValue("items", {
                        type: "object",
                        properties: {},
                    });
                }
            }
        }
    }, [form, currentIType]);

    const handleNameBlur = () => {
        const currentValues = form.getFieldsValue();
        // const nameValue = currentValues.name;
        const nameValue = snakeCase(currentValues.name);

        if (nameValue && nameValue !== value.name) {
            onChange({
                ...currentValues,
                name: nameValue,
                _nameChanged: true,
            });
        }
    };
    const handleValuesChange = (changedValues, allValues) => {
        if (changedValues.itype) {
            const newIType = changedValues.itype;

            // Set "by" based on itype
            if (newIType === "string" || newIType === "html") {
                allValues.by = "ai";
            } else if (newIType === "img") {
                // For image type, preserve existing "by" value if it exists
                const currentBy = form.getFieldValue("by");
                if (!currentBy) {
                    // Only set default if no existing value
                    allValues.by = "ai";
                }
            }
            if (newIType === "array") {
                allValues.type = "array";
                setShowItemsSchema(true);
                if (!allValues.items) {
                    allValues.items = { type: "object", properties: {} };
                }
            } else {
                // For non-array types
                allValues.type = "string";
                setShowItemsSchema(false);
                delete allValues.items;
            }
        }

        // Handle image_type radio changes
        if (changedValues.image_type) {
            allValues.by =
                changedValues.image_type === "user_uploaded" ? "ui" : "ai";
        }
        // if (changedValues.type) {
        //   const newType = changedValues.type;
        //   setShowItemsSchema(newType === "array");
        //   if (newType === "array" && !allValues.items) {
        //     allValues.items = { type: "object", properties: {} };
        //   }
        //   if (newType !== "array") {
        //     delete allValues.items;
        //   }
        // }
        if (
            !changedValues.by &&
            !changedValues.itype &&
            !changedValues.image_type
        ) {
            // Preserve the existing "by" value when other fields change
            allValues.by = form.getFieldValue("by") || value?.by;
        }
        const { image_type, ...cleanValues } = allValues;
        const finalType = cleanValues.itype === "array" ? "array" : "string";
        onChange({ ...cleanValues, type: finalType });
    };
    const handleItemsChange = (newItems) => {
        const hasNameChangeComplete = Object.values(newItems).some(
            (item) => item._nameChanged
        );
        let updatedItems;

        if (hasNameChangeComplete) {
            updatedItems = {};
            Object.entries(newItems).forEach(([key, value]) => {
                if (value._nameChanged) {
                    const newName =
                        value.name?.toLowerCase().replace(/\s+/g, "_") || key;
                    const { _nameChanged, ...cleanValue } = value;
                    updatedItems[newName] = cleanValue;
                } else {
                    updatedItems[key] = value;
                }
            });
        } else {
            updatedItems = { ...newItems };
        }

        setItemsProperties(updatedItems);

        const currentValues = form.getFieldsValue();
        const updatedValue = {
            ...currentValues,
            items: {
                ...currentValues.items,
                properties: updatedItems,
            },
        };

        form.setFieldsValue(updatedValue);
        onChange(updatedValue);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={
                typeof value === "string"
                    ? { name: value, type: "text", label: value }
                    : value
            }
            onValuesChange={handleValuesChange}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                }}
            >
                <Form.Item required label="Name" name="name">
                    <Input onBlur={handleNameBlur} disabled={is_origin} />
                </Form.Item>
                {/* <Form.Item label="Type" name="type">
            <Select
            options={[
            { value: "text", label: "Text" },
            { value: "textarea", label: "Textarea" },
            { value: "image", label: "Image" },
            { value: "array", label: "Array" },
            ]}
            />
            </Form.Item> */}
            </div>

            <Form.Item label="Description" name="description">
                <TextArea />
            </Form.Item>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                }}
            >
                <Form.Item label="itype" name="itype">
                    <Select
                        options={[
                            { value: "string", label: "String" },
                            { value: "number", label: "Number" },
                            { value: "html", label: "HTML" },
                            { value: "html_p", label: "HTML_P" },
                            { value: "html_li", label: "HTML_Li" },
                            { value: "icon", label: "Icon" },
                            { value: "img", label: "Image" },
                            { value: "array", label: "Array" },
                        ]}
                        disabled={is_origin}
                    />
                </Form.Item>
            </div>
            {currentIType === "img" && (
                <>
                    <Row style={{ gap: "50px" }}>
                        <Form.Item label="Image Type" name="image_type">
                            <Radio.Group
                                onChange={(e) => {
                                    const value = e.target.value;
                                    form.setFieldValue(
                                        "by",
                                        value === "user_uploaded" ? "ui" : "ai"
                                    );
                                }}
                            >
                                <Radio value="user_uploaded">
                                    User Uploaded
                                </Radio>
                                <Radio value="ai_generated">AI Generated</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="Bg Remove" name="bg_rm">
                            <Radio.Group
                                onChange={(e) => {
                                    const value = e.target.value;
                                    form.setFieldValue("bg_rm", value === true);
                                }}
                            >
                                <Radio value={true}>Yes</Radio>
                                <Radio value={false}>No</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Row>

                    <Row style={{ gap: "50px" }}>
                        <Form.Item
                            label="Image Style"
                            name="image_style"
                            initialValue="null"
                        >
                            <Select
                                loading={imageStyleLoading}
                                style={{ width: "200px" }}
                            >
                                {imageStyleSuccess &&
                                    imageStyleData?.map((item) => (
                                        <Select.Option
                                            key={item.id}
                                            value={item.name}
                                        >
                                            {item.name}
                                        </Select.Option>
                                    ))}
                            </Select>
                            {/* <Select
                                style={{ width: "200px" }}
                                options={[
                                    {
                                        value: "illustration",
                                        label: "Illustration",
                                    },
                                    { value: "icon_line", label: "Icon Line" },
                                    {
                                        value: "icon_solid",
                                        label: "Icon Solid",
                                    },
                                    { value: "icon_flat", label: "Icon Flat" },
                                    {
                                        value: "icon_handdrawn",
                                        label: "Icon Handdrawn",
                                    },
                                    { value: "handdrawn", label: "Handdrawn" },
                                    { value: "3d_design", label: "3D Design" },
                                ]}
                            /> */}
                        </Form.Item>
                        <Form.Item
                            label="Aspect Ratio"
                            name="aspect_ratio"
                            initialValue="1:1"
                        >
                            <Select
                                style={{ width: "200px" }}
                                options={[
                                    {
                                        value: "1:1",
                                        label: "Square",
                                    },
                                    {
                                        value: "3:4",
                                        label: "Portrait 3:4",
                                    },
                                    {
                                        value: "9:16",
                                        label: "Portrait 9:16",
                                    },
                                    {
                                        value: "4:3",
                                        label: "Lanscape 4:3",
                                    },
                                    {
                                        value: "16:9",
                                        label: "Landscape 16:9",
                                    },
                                ]}
                            />
                        </Form.Item>
                    </Row>
                    <Form.Item
                        label="Object Position"
                        name="object_position"
                        initialValue="null"
                    >
                        <Select
                            style={{ width: "200px" }}
                            options={[
                                { value: "left", label: "Left" },
                                { value: "right", label: "Right" },
                                { value: "top", label: "Top" },
                                { value: "bottom", label: "Bottom" },
                            ]}
                        />
                    </Form.Item>
                </>
            )}
            {currentIType &&
                (currentIType === "string" ||
                    currentIType === "html" ||
                    currentIType === "html_li" ||
                    currentIType === "html_p") && (
                    <Row style={{ gap: "10px" }}>
                        <Form.Item label="Max Characters" name="maxChar">
                            <Input type="number" disabled={is_origin} />
                        </Form.Item>
                        <Form.Item label="Min Characters" name="minChar">
                            <Input type="number" disabled={is_origin} />
                        </Form.Item>
                        <Form.Item
                            label="theme"
                            name="theme"
                            initialValue="light"
                        >
                            <Select
                                options={[
                                    { value: "dark", label: "dark" },
                                    { value: "light", label: "light" },
                                ]}
                                defaultValue={"dark"}
                            />
                        </Form.Item>
                    </Row>
                )}
            {/* {currentType === "image" && (
                <Form.Item
                    label="Remove Background"
                    name="bg_rm"
                    valuePropName="checked"
                >
                    <Select
                        options={[
                            { value: true, label: "Yes" },
                            { value: false, label: "No" },
                        ]}
                        allowClear
                    />
                </Form.Item>
            )} */}

            {currentType === "textarea" && (
                <Form.Item label="Max Characters" name="maxChar">
                    <Input type="number" disabled={is_origin} />
                </Form.Item>
            )}
            {showItemsSchema && (
                <div
                    style={{
                        marginTop: "20px",
                        padding: "15px",
                        border: "1px dashed #ccc",
                        borderRadius: "5px",
                        backgroundColor: "#f9f9f9",
                    }}
                >
                    <ArrayItemsForm
                        value={itemsProperties}
                        onChange={handleItemsChange}
                    />
                </div>
            )}
        </Form>
    );
}

function UserInputsField({
    value = [],
    onAddInput,
    onUpdateInput,
    onRemoveInput,
}) {
    return (
        <>
            {value?.length > 0 && (
                <Collapse
                    style={{ marginBottom: "10px" }}
                    accordion={true}
                    items={value.map((item, index) => ({
                        key: `input-${index}`,
                        label: (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span>
                                    {item?.label || `Input ${index + 1}`}
                                </span>
                                <Button
                                    size="small"
                                    danger
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveInput(index);
                                    }}
                                >
                                    {getHeroIcon("minus", { width: "18px" })}
                                </Button>
                            </div>
                        ),
                        children: (
                            <UserInputOneField
                                value={item}
                                onChange={(updatedValue) =>
                                    onUpdateInput(index, updatedValue)
                                }
                            />
                        ),
                    }))}
                />
            )}
            <Button
                onClick={onAddInput}
                style={{ marginTop: "0px" }}
                icon={<PlusOutlined />}
            >
                Add Input
            </Button>
        </>
    );
}

function UserInputOneField({ value, onChange }) {
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue(value);
    }, [form, value]);

    const handleValuesChange = (_, allValues) => {
        onChange(allValues);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={value}
            onValuesChange={handleValuesChange}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                }}
            >
                <Form.Item required label="Name" name="name">
                    <Input
                        onBlur={(e) => {
                            const nameValue = snakeCase(e.target.value);
                            form.setFieldValue("name", nameValue);
                            onChange({ ...value, name: nameValue });
                        }}
                    />
                </Form.Item>
                <Form.Item label="Type" name="type">
                    <Select
                        options={[
                            { value: "text", label: "Text" },
                            { value: "textarea", label: "Textarea" },
                            { value: "image", label: "Image" },
                            { value: "date", label: "Date" },
                        ]}
                        defaultValue={"text"}
                    />
                </Form.Item>
            </div>
            <Form.Item label="Question" name="label">
                <Input />
            </Form.Item>
        </Form>
    );
}

export default PresetForm;
